import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import Card from "../ui/Card";
import DateRangePicker from "../ui/DateRangePicker";
import dayjs from "dayjs";

export default function CustomerBillingDashboard({ customerId }) {
  const [dateRange, setDateRange] = useState({
    from: dayjs().startOf("month").toDate(),
    to: dayjs().endOf("month").toDate(),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeReportingGaps, setTimeReportingGaps] = useState(0);

  useEffect(() => {
    if (!customerId || !dateRange?.from || !dateRange?.to) return;
    setLoading(true);
    setError(null);
    const from = dayjs(dateRange.from).format("YYYY-MM-DD");
    const to = dayjs(dateRange.to).format("YYYY-MM-DD");
    axiosInstance
      .get(`/admin/customers/${customerId}/billing-overview`, {
        params: { from, to },
      })
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error(err);
        setError("Failed to load billing overview");
      })
      .finally(() => setLoading(false));

    axiosInstance
      .get(`/admin/customers/${customerId}/time-reporting-overview`, {
        params: { from, to },
      })
      .then((res) => setTimeReportingGaps(res.data?.summary?.withGapsCount ?? 0))
      .catch(() => setTimeReportingGaps(0));
  }, [customerId, dateRange]);

  const summary = data?.summary || {};
  const organisations = data?.organisations || [];

  return (
    <Card className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Billing overview</h3>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          placeholder="Select range"
        />
      </div>

      {timeReportingGaps > 0 && (
        <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          {timeReportingGaps} {timeReportingGaps === 1 ? "member has" : "members have"} unregistered
          working days in this period — review time reporting first.
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-600">Loading billing data…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <StatChip
              label="Unbilled hours"
              value={summary.totalUnbilledHours?.toFixed?.(1) ?? summary.totalUnbilledHours ?? "0"}
            />
            <StatChip
              label="Pending invoices"
              value={summary.pendingInvoiceCount ?? 0}
            />
            <StatChip
              label="Departments"
              value={organisations.length}
            />
          </div>

          {organisations.length > 0 && (
            <div className="overflow-x-auto">
              <table className="table-auto w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Department</th>
                    <th className="text-right p-2">Unbilled hours</th>
                    <th className="text-right p-2">Pending invoices</th>
                    <th className="text-left p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {organisations.map((org) => (
                    <tr key={org._id} className="border-t">
                      <td className="p-2">{org.name}</td>
                      <td className="p-2 text-right">
                        {org.unbilledHours?.toFixed?.(1) ?? org.unbilledHours ?? 0}
                      </td>
                      <td className="p-2 text-right">{org.pendingInvoiceCount ?? 0}</td>
                      <td className="p-2">
                        <Link
                          to="/admin/billing"
                          className="text-blue-600 hover:underline text-xs"
                        >
                          View billing
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}
